import { useState, useEffect, useMemo } from "react";
import { format } from "date-fns";
import {
    useTheme,
    useMediaQuery,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    TableContainer,
    Paper,
    Chip,
    MenuItem,
    Box,
    IconButton,
    Collapse,
    Badge,
    Typography,
    FormControl,
    Select,
    Pagination,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import {
    ArrowUpward as ArrowUpwardIcon,
    ArrowDownward as ArrowDownwardIcon,
    FirstPage as FirstPageIcon,
    LastPage as LastPageIcon,
    ExpandMore as ExpandMoreIcon,
} from "@mui/icons-material";
import { LAYOUT_HEIGHT } from 'state/constants';


const DynamicTable = ({
    data = [],
    columns,
    pageSize = 10,
    sortable = true,
    groupByField,
    onRowClick,
}) => {
    const theme = useTheme();
    const backgroundDefault = theme.palette.background.default;
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
    const maxHeight = `calc(${LAYOUT_HEIGHT} - 3.2rem)`;

    const [currentPage, setCurrentPage] = useState(1);
    const [sortConfig, setSortConfig] = useState(null);
    const [expandedGroups, setExpandedGroups] = useState([]);
    const [pageSizeOptions] = useState([25, 50, 100]);
    const [currentPageSize, setCurrentPageSize] = useState(pageSize);
    const [selectedKey, setSelectedKey] = useState(null);

    // Auto-detect columns if not provided
    const autoDetectedColumns = useMemo(() => {
        if (columns) return columns;
        if (data.length === 0) return [];
        const keys = Array.from(new Set(data.flatMap((item) => Object.keys(item))));
        return keys.map((key) => {
            const type = detectDataType(data.find((item) => item[key] !== undefined)?.[key]);
            return {
                key,
                label: key.charAt(0).toUpperCase() + key.replace(/([A-Z])/g, " $1").slice(1),
                type,
                sortable: type !== "object" && type !== "unknown",
                searchable: type !== "object" && type !== "unknown",
            };
        });
    }, [data, columns]);

    // Get the active column definitions
    const activeColumns = useMemo(() => {
        const cols = columns || autoDetectedColumns;
        return cols.map((col) => ({
            ...col,
            searchable: col.searchable !== undefined ? col.searchable : col.type !== "object" && col.type !== "unknown",
            sortable: col.sortable !== undefined ? col.sortable : col.type !== "object" && col.type !== "unknown",
        }));
    }, [columns, autoDetectedColumns]);
    
    function detectDataType(value) {
        if (value === null || value === undefined) return "unknown";
        if (Array.isArray(value)) return "array";
        if (value instanceof Date) return "date";
        const type = typeof value;
        if (type === "string") {
            const datePattern = /^\d{4}-\d{2}-\d{2}(T\d{2}:\d{2}:\d{2}(\.\d{3})?Z?)?$/;
            if (datePattern.test(value) && !isNaN(Date.parse(value))) return "date";
                return "string";
        }
        if (type === "number") return "number";
        if (type === "boolean") return "boolean";
        if (type === "object") return "object";
        return "unknown";
    }
  
    function formatValue(value, type) {
        if (value === null || value === undefined) return "";
        switch (type) {
            case "date":
                const date = value instanceof Date ? value : new Date(value);
                return isNaN(date.getTime()) ? String(value) : format(date, "dd/MM/yy");
            case "array":
                return Array.isArray(value) ? value.join(", ") : String(value);
            case "object":
                return JSON.stringify(value);
            default:
                return String(value);
        }
    }

    const sortedAndGroupedData = useMemo(() => {
        let result = [...data];
        if (sortConfig) {
            result.sort((a, b) => {
                const aValue = a[sortConfig.key];
                const bValue = b[sortConfig.key];
                if (aValue === undefined || aValue === null) return sortConfig.direction === "asc" ? -1 : 1;
                if (bValue === undefined || bValue === null) return sortConfig.direction === "asc" ? 1 : -1;
                const columnType = activeColumns.find((col) => col.key === sortConfig.key)?.type || "string";
                if (columnType === "date") {
                    const dateA = aValue instanceof Date ? aValue : new Date(aValue);
                    const dateB = bValue instanceof Date ? bValue : new Date(bValue);
                    return sortConfig.direction === "asc" ? dateA.getTime() - dateB.getTime() : dateB.getTime() - dateA.getTime();
                }
                if (columnType === "number") {
                    return sortConfig.direction === "asc" ? Number(aValue) - Number(bValue) : Number(bValue) - Number(aValue);
                }
                const strA = String(aValue).toLowerCase();
                const strB = String(bValue).toLowerCase();
                return sortConfig.direction === "asc" ? strA.localeCompare(strB) : strB.localeCompare(strA);
            });
        }
        if (groupByField) {
        const groups = {};
        result.forEach((item) => {
            const groupValue = item[groupByField];
            const column = activeColumns.find((col) => col.key === groupByField);
            const type = column?.type || detectDataType(groupValue);
            let groupKey = groupValue === null || groupValue === undefined ? "No value" : groupValue;
            if (type === "date" && groupKey !== "No value") {
                const date = groupKey instanceof Date ? groupKey : new Date(groupKey);
                groupKey = isNaN(date.getTime()) ? String(groupKey) : format(date, "dd/MM/yy");
            } else {
                groupKey = String(groupKey);
            }
            if (!groups[groupKey]) groups[groupKey] = [];
            groups[groupKey].push(item);
        });
        result = Object.entries(groups).map(([groupKey, items]) => ({
            __isGroupRow: true,
            __groupKey: groupKey,
            __groupField: groupByField,
            __itemCount: items.length,
            __items: items,
        }));
        }
        return result;
    }, [data, sortConfig, groupByField, activeColumns]);

    // Pagination
    const totalPages = Math.ceil(sortedAndGroupedData.length / currentPageSize);
    const paginatedData = useMemo(() => {
        const startIndex = (currentPage - 1) * currentPageSize;
        return sortedAndGroupedData.slice(startIndex, startIndex + currentPageSize);
    }, [sortedAndGroupedData, currentPage, currentPageSize]);
  
    const handlePageChange = (event, page) => {
        setCurrentPage(Math.max(1, Math.min(page, totalPages)));
    };
   
    const handleSort = (key) => {
        if (!sortable || key === "__rowNumber") return;
        const column = activeColumns.find((col) => col.key === key);
        if (!column?.sortable) return;
        setSortConfig((current) => {
            if (current?.key === key) {
                return current.direction === "asc" ? { key, direction: "desc" } : null;
            }
            return { key, direction: "asc" };
        });
    };

    // Render cell value based on type
    const renderCellValue = (row, columnKey, type, rowIndex, isNested = false) => {
        if (columnKey === "__rowNumber") {
            if (row.__isGroupRow && !isNested) {
                return null; // Group headers don't show a number
            }
            const baseIndex = (currentPage - 1) * currentPageSize;
            return isNested ? null : baseIndex + rowIndex + 1; // Sequential number for top-level or nested rows
        }
        if (row.__isGroupRow && columnKey === row.__groupField) {
            return (
                <Typography variant="subtitle2">
                    Group: {row.__groupKey} ({row.__itemCount} items)
                </Typography>
            );
        }
        if (row.__isGroupRow) return null;
        const value = row[columnKey];
        if (value === null || value === undefined) return null;
        switch (type) {
            case "boolean":
                return <Chip label={value ? "Yes" : "No"} color={value ? "primary" : "default"} size="small" />;
            case "array":
                if (!Array.isArray(value)) return formatValue(value, type);
                return (
                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                        {value.slice(0, 3).map((item, i) => (
                        <Chip key={i} label={String(item)} size="small" variant="outlined" />
                        ))}
                        {value.length > 3 && <Chip label={`+${value.length - 3}`} size="small" variant="outlined" />}
                    </Box>
                );
            case "date":
                const date = value instanceof Date ? value : new Date(value);
                return isNaN(date.getTime()) ? String(value) : format(date, "dd/MM/yy");
            default:
                return formatValue(value, type);
        }
    };

    // Update current page if total pages change
    useEffect(() => {
        const newTotalPages = Math.ceil(sortedAndGroupedData.length / currentPageSize);
        if (currentPage > newTotalPages) {
            setCurrentPage(Math.max(1, newTotalPages));
        }
    }, [sortedAndGroupedData, currentPageSize, currentPage]);

    return (
        <Box border={`1px solid ${theme.palette.neutral.deep}`}>
            {/* Table with Scrollbar */}
            <TableContainer
                component={Paper}
                sx={{ maxHeight }}
            >
                <Table stickyHeader>
                    <TableHead>
                        <TableRow>
                            {activeColumns.map((column) => (
                                <TableCell
                                    key={column.key}
                                    sx={{
                                        cursor: column.sortable && sortable && column.key !== "__rowNumber" ? "pointer" : "default",
                                        backgroundColor: theme.palette.background.paper,
                                        position: "sticky",
                                        top: 0,
                                        zIndex: 1,
                                        width: column.key === "__rowNumber" ? "60px" : "auto",                                        
                                    }}
                                    onClick={() => column.sortable && sortable && column.key !== "__rowNumber" && handleSort(column.key)}
                                >
                                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                                        <Typography variant="body2">{column.label}</Typography>
                                        {sortable && column.sortable && sortConfig?.key === column.key && (
                                            sortConfig.direction === "asc" ? (
                                                <ArrowUpwardIcon fontSize="small" />
                                            ) : (
                                                <ArrowDownwardIcon fontSize="small" />
                                            )
                                        )}
                                        {groupByField === column.key && <Badge badgeContent="Grouped" color="secondary" sx={{ ml: 3 }} />}
                                    </Box>
                                </TableCell>
                            ))}
                        </TableRow>
                    </TableHead>
                    <TableBody sx={{ bgcolor: backgroundDefault }}>
                        {paginatedData.map((row, rowIndex) => (
                            row.__isGroupRow 
                                ? (<TableRow key={`group-${rowIndex}`}>
                                    <TableCell colSpan={activeColumns.length}>
                                        <Box sx={{ display: "flex", alignItems: "center" }}>
                                            <IconButton
                                                onClick={() => {
                                                    setExpandedGroups((prev) =>
                                                        prev.includes(row.__groupKey)
                                                            ? prev.filter((key) => key !== row.__groupKey)
                                                            : [...prev, row.__groupKey]
                                                    );
                                                }}
                                            >
                                                <ExpandMoreIcon
                                                    sx={{
                                                        transform: expandedGroups.includes(row.__groupKey) ? "rotate(180deg)" : "none",
                                                        transition: "transform 0.2s",
                                                    }}
                                                />
                                            </IconButton>
                                            <Typography variant="subtitle2">
                                                {row.__groupField}: {row.__groupKey} ({row.__itemCount} items)
                                            </Typography>
                                        </Box>
                                        <Collapse in={expandedGroups.includes(row.__groupKey)}>
                                            <Table>
                                                <TableBody>
                                                    {row.__items.map((item, itemIndex) => (
                                                        <TableRow key={`${rowIndex}-${itemIndex}`}>
                                                            {activeColumns.map((column) => {
                                                                const type = column.type || detectDataType(item[column.key]);
                                                                return (
                                                                    <TableCell 
                                                                        key={`${rowIndex}-${itemIndex}-${column.key}`}
                                                                        sx={{
                                                                            maxWidth: column.maxWidth ? column.maxWidth : "none",
                                                                            overflow: column.maxWidth ? "hidden" : "visible",
                                                                            textOverflow: column.maxWidth ? "ellipsis" : "clip",
                                                                            whiteSpace: column.maxWidth ? "nowrap" : "normal",
                                                                        }}
                                                                    >
                                                                        {column.format
                                                                            ? column.format(item[column.key])
                                                                            : renderCellValue(item, column.key, type, rowIndex + itemIndex, true)}
                                                                    </TableCell>
                                                                );
                                                            })}
                                                        </TableRow>
                                                    ))}
                                                </TableBody>
                                            </Table>
                                        </Collapse>
                                    </TableCell>
                                </TableRow>
                            ) : (
                                <TableRow
                                    key={rowIndex}
                                    onClick={() => { setSelectedKey(rowIndex); onRowClick?.(row); }}
                                    sx={{
                                        cursor: onRowClick ? 'pointer' : 'default',
                                        bgcolor: selectedKey === rowIndex
                                            ? alpha(theme.palette.primary.main, 0.12)
                                            : 'transparent',
                                        animation: selectedKey === rowIndex ? 'rowSelect 0.3s ease-out' : 'none',
                                        '@keyframes rowSelect': {
                                            from: { backgroundColor: alpha(theme.palette.primary.main, 0.38) },
                                            to:   { backgroundColor: alpha(theme.palette.primary.main, 0.12) },
                                        },
                                        '&:hover': {
                                            bgcolor: selectedKey === rowIndex
                                                ? alpha(theme.palette.primary.main, 0.18)
                                                : 'action.hover',
                                        },
                                        transition: 'background-color 0.15s ease',
                                    }}
                                >
                                    {activeColumns?.map((column) => {
                                        const type = column.type || detectDataType(row[column.key]);
                                        return (
                                            <TableCell
                                                key={`${rowIndex}-${column.key}`}
                                                sx={{
                                                    maxWidth: column.maxWidth ? column.maxWidth : "none",
                                                    overflow: column.maxWidth ? "hidden" : "visible",
                                                    textOverflow: column.maxWidth ? "ellipsis" : "clip",
                                                    whiteSpace: column.maxWidth ? "nowrap" : "normal",
                                                }}
                                            >
                                                {column.format
                                                    ? column.format(row[column.key])
                                                    : renderCellValue(row, column.key, type, rowIndex)}
                                            </TableCell>
                                        );
                                    })}
                                </TableRow>
                            )
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>

            {/* Pagination */}
            {totalPages > 1 && (
                <Box
                    height="3rem"
                    sx={{
                        display: "flex",
                        justifyContent: isMobile ? "center" : "space-between",
                        alignItems: "center",
                        px: 1,
                    }}
                >
                    {!isMobile && (
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                            <Typography variant="body2" color="text.secondary">
                                Rows per page:
                            </Typography>
                            <FormControl size="small" sx={{ minWidth: 68 }}>
                                <Select
                                    value={currentPageSize.toString()}
                                    onChange={(e) => {
                                        const newPageSize = Number.parseInt(e.target.value);
                                        setCurrentPageSize(newPageSize);
                                        setCurrentPage(1);
                                    }}
                                >
                                    {pageSizeOptions.map((size) => (
                                        <MenuItem key={size} value={size.toString()}>
                                            {size}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>                            
                        </Box>
                    )}
                    {!isMobile && (
                        <Typography variant="body2" color="text.secondary">
                            Showing {(currentPage - 1) * currentPageSize + 1} to{" "}
                            {Math.min(currentPage * currentPageSize, sortedAndGroupedData.length)} of {sortedAndGroupedData.length} rows
                        </Typography>
                    )}
                    <Box sx={{ display: "flex", alignItems: "center" }}>
                        <IconButton onClick={() => handlePageChange(null, 1)} disabled={currentPage === 1}>
                            <FirstPageIcon />
                        </IconButton>
                        <Pagination
                            size="small"
                            count={totalPages}
                            page={currentPage}
                            onChange={handlePageChange}
                            sx={{ display: "inline-flex" }}
                        />
                        <IconButton onClick={() => handlePageChange(null, totalPages)} disabled={currentPage === totalPages}>
                            <LastPageIcon />
                        </IconButton>
                    </Box>
                </Box>
            )}
        </Box>
    );
};

export default DynamicTable;